import { supabase } from '../lib/supabase';

/**
 * Fetcha tutti i dati per AppInstallTemplate.
 * Accetta uno o più campaign UUID — rileva automaticamente Android/iOS dal nome.
 *
 * @param {string|string[]} campaignIds - UUID singolo o array di campagne
 * @returns {Promise<object>} data shape compatibile con AppInstallTemplate
 *
 * Esempi:
 *   fetchAppInstallData('uuid-android')
 *   fetchAppInstallData(['uuid-android', 'uuid-ios'])
 */
export async function fetchAppInstallData(campaignIds) {
  const ids = Array.isArray(campaignIds) ? campaignIds : [campaignIds];
  if (ids.length === 0) throw new Error('Almeno un campaign ID richiesto');

  // ── 1. Campagne + cliente ────────────────────────────────────────────────
  const { data: campaigns, error: campErr } = await supabase
    .from('campaigns')
    .select('id, name, start_date, end_date, client_id, clients(name)')
    .in('id', ids);

  if (campErr) throw new Error(`Campaign fetch error: ${campErr.message}`);
  if (!campaigns?.length) throw new Error('Nessuna campagna trovata');

  // Classifica ogni campagna per device
  const classify = name => {
    const n = (name || '').toLowerCase();
    if (n.includes('ios') || n.includes('iphone') || n.includes('apple')) return 'ios';
    if (n.includes('android')) return 'android';
    return 'all';
  };

  const campaignMap = Object.fromEntries(
    campaigns.map(c => [c.id, { ...c, device: classify(c.name) }])
  );

  // Campagna di riferimento per nome e cliente
  const ref = campaigns.find(c => classify(c.name) === 'all')
           || campaigns.find(c => classify(c.name) === 'android')
           || campaigns[0];

  const fmtDate = d => d
    ? new Date(d).toLocaleDateString('it-IT', { day:'numeric', month:'long', year:'numeric' })
    : '';

  const starts = campaigns.map(c => c.start_date).filter(Boolean).sort();
  const ends   = campaigns.map(c => c.end_date).filter(Boolean).sort();
  const period = starts.length
    ? `${fmtDate(starts[0])} – ${fmtDate(ends[ends.length - 1])}`
    : '';

  // ── 2. KPI ufficiali Meta ─────────────────────────────────────────────────
  const { data: kpiRows } = await supabase
    .from('campaign_meta_kpi')
    .select('reach_meta, impressions_meta, spend_meta')
    .in('campaign_id', ids);

  const kpiTotals = (kpiRows || []).reduce((acc, k) => ({
    reach:       acc.reach       + (Number(k.reach_meta)       || 0),
    impressions: acc.impressions + (Number(k.impressions_meta) || 0),
    spend:       acc.spend       + (Number(k.spend_meta)       || 0),
  }), { reach:0, impressions:0, spend:0 });

  // ── 3. Insights giornalieri ───────────────────────────────────────────────
  const { data: insights } = await supabase
    .from('adset_insights_daily')
    .select(`
      impressions, spend, cost_per_result,
      conversions, post_reactions, engagement,
      adsets!inner(id, name, campaign_id)
    `)
    .in('adsets.campaign_id', ids);

  const insightsByCampaign = {};
  (insights || []).forEach(r => {
    const cid = r.adsets?.campaign_id;
    if (!cid) return;
    if (!insightsByCampaign[cid]) insightsByCampaign[cid] = [];
    insightsByCampaign[cid].push(r);
  });

  const sumInsights = rows => (rows || []).reduce((acc, r) => ({
    impressions:  acc.impressions  + (Number(r.impressions)    || 0),
    spend:        acc.spend        + (Number(r.spend)          || 0),
    interactions: acc.interactions + (Number(r.engagement)     || 0),
    reactions:    acc.reactions    + (Number(r.post_reactions) || 0),
    installs:     acc.installs     + (Number(r.conversions)    || 0),
  }), { impressions:0, spend:0, interactions:0, reactions:0, installs:0 });

  const allInsights = Object.values(insightsByCampaign).flat();
  const totals      = sumInsights(allInsights);

  // Device split — automatico da classificazione campagna
  const androidIds = ids.filter(id => ['android','all'].includes(campaignMap[id]?.device));
  const iosIds     = ids.filter(id => ['ios','all'].includes(campaignMap[id]?.device));

  const andTot = sumInsights(androidIds.flatMap(id => insightsByCampaign[id] || []));
  const iosTot = sumInsights(iosIds.flatMap(id => insightsByCampaign[id] || []));

  // ── 4. Platform split FB/IG ───────────────────────────────────────────────
  const isIG = r => {
    const n = (r.adsets?.name || '').toLowerCase();
    return n.includes('instagram') || n.includes('_ig') || n.includes('ig_');
  };

  const sumPlat = rows => rows.reduce((acc, r) => ({
    impressions:  acc.impressions  + (Number(r.impressions)    || 0),
    interactions: acc.interactions + (Number(r.engagement)     || 0),
    reactions:    acc.reactions    + (Number(r.post_reactions) || 0),
    spend:        acc.spend        + (Number(r.spend)          || 0),
    installs:     acc.installs     + (Number(r.conversions)    || 0),
  }), { impressions:0, interactions:0, reactions:0, spend:0, installs:0 });

  const fbTot = sumPlat(allInsights.filter(r => !isIG(r)));
  const igTot = sumPlat(allInsights.filter(r =>  isIG(r)));

  const calcCpi = (s, i)   => i   > 0 ? s / i           : 0;
  const calcCpm = (s, imp) => imp > 0 ? (s / imp) * 1000 : 0;
  const calcCpe = (s, e)   => e   > 0 ? s / e           : 0;

  // ── 5. Demografica ────────────────────────────────────────────────────────
  const { data: demog } = await supabase
    .from('adset_demographics_daily')
    .select('gender, age_range, impressions, conversions')
    .in('campaign_id', ids)
    .not('age_range', 'is', null)
    .is('region', null);

  const demoMap = {};
  (demog || []).forEach(r => {
    const key = r.age_range;
    if (!key) return;
    if (!demoMap[key]) demoMap[key] = { age:key, fI:0, mI:0, fC:0, mC:0 };
    if (r.gender === 'female') { demoMap[key].fI += Number(r.impressions)||0; demoMap[key].fC += Number(r.conversions)||0; }
    if (r.gender === 'male')   { demoMap[key].mI += Number(r.impressions)||0; demoMap[key].mC += Number(r.conversions)||0; }
  });

  const ageOrder = ['18-24','25-34','35-44','45-54','55-64','65+'];
  const demographics = ageOrder.filter(a => demoMap[a]).map(a => {
    const d = demoMap[a];
    return {
      age:               a,
      femaleImpressions: d.fI,
      maleImpressions:   d.mI,
      femaleInstalls:    d.fC,
      maleInstalls:      d.mC,
      femaleRate: d.fI > 0 ? (d.fC / d.fI) * 100 : 0,
      maleRate:   d.mI > 0 ? (d.mC / d.mI) * 100 : 0,
    };
  });

  // ── 6. Regioni Android ────────────────────────────────────────────────────
  const regionCampaignIds = androidIds.length ? androidIds : ids;

  const { data: regionsRaw } = await supabase
    .from('adset_demographics_daily')
    .select('region, impressions, conversions, spend')
    .in('campaign_id', regionCampaignIds)
    .not('region', 'is', null);

  const regionMap = {};
  (regionsRaw || []).forEach(r => {
    if (!r.region) return;
    if (!regionMap[r.region]) regionMap[r.region] = { name:r.region, impressions:0, installs:0, spend:0 };
    regionMap[r.region].impressions += Number(r.impressions) || 0;
    regionMap[r.region].installs    += Number(r.conversions) || 0;
    regionMap[r.region].spend       += Number(r.spend)       || 0;
  });

  const regionsAndroid = Object.values(regionMap)
    .map(r => ({
      name:        r.name,
      impressions: r.impressions,
      installs:    r.installs,
      cpi:         calcCpi(r.spend, r.installs),
      rate:        r.impressions > 0 ? (r.installs / r.impressions) * 100 : 0,
    }))
    .sort((a, b) => b.installs - a.installs);

  // ── 7. Output finale ──────────────────────────────────────────────────────
  const reach       = kpiTotals.reach       || 0;
  const impressions = kpiTotals.impressions  || totals.impressions;
  const spent       = kpiTotals.spend        || totals.spend;
  const installs    = totals.installs;

  return {
    client:   ref.clients?.name || '',
    campaign: ref.name || '',
    period,
    overview: {
      installs,
      reach,
      impressions,
      interactions: totals.interactions,
      reactions:    totals.reactions,
      spent,
      cpi: calcCpi(spent, installs),
      cpm: calcCpm(spent, impressions),
      cpe: calcCpe(spent, totals.interactions),
      audience:    '',
      targetCover: null,
    },
    platformSplit: {
      facebook: {
        impressions:  fbTot.impressions,
        interactions: fbTot.interactions,
        reactions:    fbTot.reactions,
        installs:     fbTot.installs,
        spent:        fbTot.spend,
        cpm: calcCpm(fbTot.spend, fbTot.impressions),
        cpe: calcCpe(fbTot.spend, fbTot.interactions),
        cpi: calcCpi(fbTot.spend, fbTot.installs),
      },
      instagram: {
        impressions:  igTot.impressions,
        interactions: igTot.interactions,
        reactions:    igTot.reactions,
        installs:     igTot.installs,
        spent:        igTot.spend,
        cpm: calcCpm(igTot.spend, igTot.impressions),
        cpe: calcCpe(igTot.spend, igTot.interactions),
        cpi: calcCpi(igTot.spend, igTot.installs),
      },
    },
    devices: {
      android: {
        impressions: andTot.impressions,
        installs:    andTot.installs,
        spent:       andTot.spend,
        cpi:         calcCpi(andTot.spend, andTot.installs),
        cpm:         calcCpm(andTot.spend, andTot.impressions),
      },
      ios: {
        impressions: iosTot.impressions,
        installs:    iosTot.installs,
        spent:       iosTot.spend,
        cpi:         calcCpi(iosTot.spend, iosTot.installs),
        cpm:         calcCpm(iosTot.spend, iosTot.impressions),
      },
    },
    regionsAndroid,
    demographics,
  };
}
